// src/utils/map/rigFracFacets.ts
//
// Pure faceting for the Rig/Frac layer-local filter (EVA-63 revision). Given the
// rig/frac feature rows currently on the map and the active per-field selections,
// derive (a) each field's still-available option list and (b) the selections
// pruned to what's still available — so a dropdown only ever offers values that
// co-occur in the currently-filtered data, not the whole market domain.
//
// No React, no Mapbox, no store — unit-testable in isolation.
import type { RigFracLayerFilter } from "stores/map/mapTypes";

/** The faceted fields, in display order. Must match the MVT property names. */
export const RIG_FRAC_FACET_FIELDS: readonly (keyof RigFracLayerFilter)[] = [
  "operator",
  "county",
  "basin",
  "subBasin",
  "country",
  "stateProvince",
  "locationType",
  "rigTrajectory"
];

/**
 * The option standing in for "this feature has no rigTrajectory" (EVA-107).
 *
 * map-svc-rs omits the tile tag entirely rather than tagging an empty string, so
 * without this the ~31% of rigs with no trajectory — and every frac crew — would
 * be unfilterable: they'd match no selection and appear under no option. The
 * adapter substitutes it when reading tile properties, and buildRigFracFilter
 * turns a selection of it back into an "absent tag" predicate.
 *
 * KNOWN LIMITATION: this shares a namespace with real trajectory values. If the
 * producer ever emits a literal "n/a" as a real value, those features are tagged
 * and merge into this same option, and selecting it then matches only the
 * tag-less ones — a silent undercount, pinned by a test in
 * buildRigFracFilter.test.ts. Today's producer emits Horizontal | Directional |
 * Vertical | Other only. The collision-proof shape is a reserved sentinel
 * ("__no_rig_trajectory__") mapped to an "n/a" DISPLAY label at render time.
 */
export const RIG_FRAC_NO_VALUE_OPTION = "n/a";

/** One rig/frac feature's faceted attributes (values may be absent/null). */
export type RigFracRow = Partial<Record<keyof RigFracLayerFilter, Maybe<string>>>;

export type RigFracFacetOptions = Record<keyof RigFracLayerFilter, string[]>;

const EMPTY_SELECTION: readonly string[] = [];

/** Does a row satisfy the selections of every field EXCEPT `exclude`? */
function matchesOthers(
  row: RigFracRow,
  selections: RigFracLayerFilter,
  exclude: keyof RigFracLayerFilter
): boolean {
  for (const field of RIG_FRAC_FACET_FIELDS) {
    if (field === exclude) continue;
    const selected = selections[field] ?? EMPTY_SELECTION;
    if (selected.length === 0) continue;
    const value = row[field];
    if (value == null || !selected.includes(value)) return false;
  }
  return true;
}

/**
 * Compute, per field, the distinct values still reachable given the OTHER fields'
 * selections (standard facet semantics — a field's own selection never shrinks its
 * own option list, so a user can always deselect). Values are sorted; empty/nullish
 * are dropped.
 */
export function computeRigFracFacets(
  rows: readonly RigFracRow[],
  selections: RigFracLayerFilter
): RigFracFacetOptions {
  const result = {} as RigFracFacetOptions;
  for (const field of RIG_FRAC_FACET_FIELDS) {
    const values = new Set<string>();
    for (const row of rows) {
      if (!matchesOthers(row, selections, field)) continue;
      const value = row[field];
      if (typeof value === "string" && value.length > 0) values.add(value);
    }
    result[field] = Array.from(values).sort();
  }
  return result;
}
